import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { capture, MEET_AN_EXPERT_PAGE_EVENT } from '../../analytics/posthog';
import { openCfpScheduling } from '../../utils/FinancialDashboard';
import { fetchExperts } from '../../utils/api';
import styles from './MeetAnExpertPage.module.css';
import FeedbackDock from './FeedbackDock';
import TawkToWidget from './TawkToWidget';

const SKELETON_KEYS = [0, 1, 2, 3];
const CARD_STAGGER_BASE_MS = 80;
const CARD_STAGGER_STEP_MS = 85;
const EXPERT_CAROUSEL_QUERY = '(max-width: 768px)';

const cardDelayStyle = (index) => ({
  '--expert-card-delay': `${CARD_STAGGER_BASE_MS + index * CARD_STAGGER_STEP_MS}ms`,
});

const isCfpCalendlyUrl = (url) => typeof url === 'string' && url.includes('calendly.com');

function ExpertCardBody({ expert }) {
  const avatarUrl = expert.avatar_url?.trim();
  return (
    <>
      {avatarUrl ? (
        <img src={avatarUrl} alt={expert.name} className={styles.expertAvatar} />
      ) : (
        <div className={styles.expertAvatarPlaceholder} aria-hidden />
      )}
      <div className={styles.expertInfo}>
        <h3>{expert.name}</h3>
        {expert.title && <div className={styles.expertTitle}>{expert.title}</div>}
        <span className={styles.expertFocus}>{expert.specialty}</span>
        {expert.sentence && <div className={styles.expertSentence}>{expert.sentence}</div>}
      </div>
    </>
  );
}

function CarouselArrow({ direction }) {
  const points = direction === 'left' ? 'M19 12H5M11 6l-6 6 6 6' : 'M5 12h14M13 6l6 6-6 6';
  return (
    <svg className={styles.expertSwapIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useExpertCarousel(slideCount, resetToken) {
  const scrollerRef = useRef(null);
  const indexRef = useRef(0);
  const programmaticRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCarousel, setIsCarousel] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(EXPERT_CAROUSEL_QUERY).matches,
  );

  const syncIndex = useCallback((next) => {
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    indexRef.current = clamped;
    setActiveIndex((current) => (current === clamped ? current : clamped));
  }, [slideCount]);

  const readClosest = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const origin = el.getBoundingClientRect().left;
    let closest = 0;
    let min = Infinity;
    [...el.children].forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - origin);
      if (delta < min) {
        min = delta;
        closest = index;
      }
    });
    return closest;
  }, []);

  useEffect(() => {
    indexRef.current = 0;
    setActiveIndex(0);
    scrollerRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [slideCount, resetToken]);

  useEffect(() => {
    const media = window.matchMedia(EXPERT_CAROUSEL_QUERY);
    const el = scrollerRef.current;
    const applyMode = () => {
      setIsCarousel(media.matches);
      if (media.matches) syncIndex(readClosest());
    };
    const syncFromScroll = () => {
      if (!media.matches) return;
      if (programmaticRef.current) {
        syncIndex(indexRef.current);
        return;
      }
      syncIndex(readClosest());
    };
    const finishScroll = () => {
      programmaticRef.current = false;
      if (media.matches) syncIndex(readClosest());
    };
    applyMode();
    media.addEventListener('change', applyMode);
    el?.addEventListener('scroll', syncFromScroll, { passive: true });
    el?.addEventListener('scrollend', finishScroll);
    window.addEventListener('resize', syncFromScroll);
    return () => {
      media.removeEventListener('change', applyMode);
      el?.removeEventListener('scroll', syncFromScroll);
      el?.removeEventListener('scrollend', finishScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, [readClosest, syncIndex]);

  const goTo = useCallback((next) => {
    const el = scrollerRef.current;
    if (!el || !window.matchMedia(EXPERT_CAROUSEL_QUERY).matches) return;
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    const slide = el.children[clamped];
    if (!slide) return;
    programmaticRef.current = true;
    syncIndex(clamped);
    const left = slide.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    window.setTimeout(() => {
      if (!programmaticRef.current) return;
      programmaticRef.current = false;
      syncIndex(indexRef.current);
    }, 520);
  }, [slideCount, syncIndex]);

  return { scrollerRef, activeIndex, isCarousel, goTo, indexRef };
}

const ExpertCard = memo(function ExpertCard({ expert, onCardClick, index, isSlide, slideCount }) {
  const calendarUrl = expert.calendar_url?.trim();
  const linkedInUrl = expert.linkedin_url?.trim();

  return (
    <div
      className={`${styles.expertCard} ${calendarUrl ? '' : styles.expertCardNotClickable}`}
      style={cardDelayStyle(index)}
      role={isSlide ? 'group' : undefined}
      aria-roledescription={isSlide ? 'slide' : undefined}
      aria-label={isSlide ? `${index + 1} of ${slideCount}` : undefined}
    >
      {calendarUrl ? (
        <button type="button" onClick={() => onCardClick(expert)} className={styles.expertCardButton}>
          <ExpertCardBody expert={expert} />
        </button>
      ) : (
        <div className={styles.expertCardButton}>
          <ExpertCardBody expert={expert} />
        </div>
      )}
      {linkedInUrl && (
        <a
          href={linkedInUrl}
          className={styles.expertLinkedInFooter}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Connect with ${expert.name} on LinkedIn`}
        >
          LinkedIn
          <span className={styles.expertLinkedInIcon} aria-hidden>↗</span>
        </a>
      )}
    </div>
  );
});

const ExpertCardSkeleton = memo(function ExpertCardSkeleton({ index }) {
  return (
    <div
      className={`${styles.expertCard} ${styles.expertCardSkeleton}`}
      style={cardDelayStyle(index)}
      aria-hidden
    />
  );
});

const MeetAnExpertPage = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const pageRef = useRef(null);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const slideCount = loading ? SKELETON_KEYS.length : experts.length;
  const { scrollerRef, activeIndex, isCarousel, goTo, indexRef } = useExpertCarousel(slideCount, loading);
  const showCarouselControls = isCarousel && slideCount > 1;

  useEffect(() => {
    window.scrollTo(0, 0);
    const controller = new AbortController();

    fetchExperts(controller.signal).then((data) => {
      if (controller.signal.aborted) return;
      if (data) setExperts(data);
      setLoading(false);
    });

    return () => controller.abort();
  }, []);

  const handleExpertCardClick = useCallback((expert) => {
    const calendarUrl = expert.calendar_url?.trim();
    if (!calendarUrl) return;

    capture(posthog, MEET_AN_EXPERT_PAGE_EVENT, {
      action: 'expert_card',
      expert_id: expert.id,
      expert_name: expert.name,
    });

    if (isCfpCalendlyUrl(calendarUrl)) {
      openCfpScheduling(navigate, calendarUrl);
      return;
    }
    window.open(calendarUrl, '_blank', 'noopener,noreferrer');
  }, [navigate, posthog]);

  return (
    <div className={styles.meetExpertPage} ref={pageRef}>
      <main className={styles.mainContent}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeading}>
            <h2 className={styles.sectionTitle}>Meet an Expert</h2>
            <p className={styles.sectionSubtitle}>
              Book a session with a certified financial planner for personalized guidance.
            </p>
          </div>
        </div>
        <div className={styles.expertCarousel}>
          <div
            className={styles.expertGrid}
            ref={scrollerRef}
            tabIndex={showCarouselControls ? 0 : undefined}
            role={isCarousel ? 'region' : undefined}
            aria-roledescription={isCarousel ? 'carousel' : undefined}
            aria-label={isCarousel ? (loading ? 'Loading experts' : 'Meet an expert') : undefined}
            aria-busy={isCarousel && loading ? true : undefined}
            onKeyDown={showCarouselControls ? (event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                goTo(indexRef.current + 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                goTo(indexRef.current - 1);
              }
            } : undefined}
          >
            {loading
              ? SKELETON_KEYS.map((i) => <ExpertCardSkeleton key={i} index={i} />)
              : experts.map((expert, index) => (
                  <ExpertCard
                    key={expert.id}
                    expert={expert}
                    index={index}
                    isSlide={isCarousel}
                    slideCount={experts.length}
                    onCardClick={handleExpertCardClick}
                  />
                ))}
          </div>
          {showCarouselControls && (
            <div className={styles.expertNav}>
              <button
                type="button"
                className={styles.expertSwap}
                aria-label="Previous expert"
                disabled={activeIndex === 0}
                onClick={() => goTo(activeIndex - 1)}
              >
                <CarouselArrow direction="left" />
              </button>
              <div className={styles.expertDots}>
                {(loading ? SKELETON_KEYS : experts).map((item, index) => {
                  const label = loading ? `Expert ${index + 1}` : item.name;
                  return (
                    <button
                      key={loading ? item : item.id}
                      type="button"
                      className={index === activeIndex ? `${styles.expertDot} ${styles.expertDotActive}` : styles.expertDot}
                      aria-label={label}
                      aria-current={index === activeIndex ? 'true' : undefined}
                      onClick={() => goTo(index)}
                    />
                  );
                })}
              </div>
              <button
                type="button"
                className={styles.expertSwap}
                aria-label="Next expert"
                disabled={activeIndex >= slideCount - 1}
                onClick={() => goTo(activeIndex + 1)}
              >
                <CarouselArrow direction="right" />
              </button>
              {!loading && experts[activeIndex] && (
                <p className={styles.expertLive} aria-live="polite">
                  {experts[activeIndex].name}, {activeIndex + 1} of {experts.length}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
      <TawkToWidget themeRootRef={pageRef} />
      <FeedbackDock source="meet_an_expert" />
    </div>
  );
};

export default MeetAnExpertPage;
